import {
  Checkbox,
  CheckboxGroup,
  Description,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  NumberField,
  Select,
  TextField,
} from '@heroui/react';
import type { AttributeDef } from '../data/api.ts';

interface Props {
  def: AttributeDef;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

/**
 * The key of the "clear the selection" row in a single-select.
 *
 * The old `<select>` cleared itself with `<option value="">`, which HTML gives
 * away for free. RAC has no deselect gesture, so the blank row has to be a real
 * collection item with a real `Key` — and an empty string is the one key that is
 * indistinguishable from "no key at all" in every falsy test between here and the
 * collection builder. The sentinel is explicit instead, and is mapped back to
 * `null` before anything leaves this file: the wire format is unchanged.
 */
const CLEAR_KEY = '__attribute_field_clear__';

/**
 * One form control for one attribute definition.
 *
 * The catalogue is user-defined data (DECISION-9), so the control has to be
 * chosen at runtime from `valueType` rather than written per field. Adding a
 * new attribute in the admin UI makes an input appear here with no code change
 * — that is the whole point of the design.
 *
 * Every branch below is a HeroUI field ROOT, which already carries the
 * label/description/control column layout that `div.field` used to provide.
 * The label and the hint therefore live INSIDE the field rather than beside it:
 * RAC generates the ids and wires `htmlFor` / `aria-describedby` from the root,
 * which is what replaced the hand-written `id={`attr-${def.key}`}`.
 *
 * Nothing here changes the value the server receives. Where a HeroUI control
 * would have, the native one is kept and the reason is written above it.
 */
export function AttributeField({ def, value, onChange, disabled }: Props) {
  // Identical in all six branches; only the control differs.
  const label = (
    <Label>
      {def.label}
      {def.unit && <span className="font-normal text-muted"> ({def.unit})</span>}
    </Label>
  );
  const description = def.description ? <Description>{def.description}</Description> : null;

  switch (def.valueType) {
    case 'number': {
      // `NaN` is RAC's empty number, `null` is what the API stores. The two are
      // mapped at this boundary and nowhere else.
      const current =
        value === null || value === undefined || value === '' ? Number.NaN : Number(value);
      return (
        <NumberField
          value={current}
          onChange={(next) => onChange(Number.isNaN(next) ? null : next)}
          isDisabled={disabled}
          fullWidth
          /* Pinned, not decorative. NumberField commits on blur by parsing its
             own FORMATTED text back to a number, and Intl's default is three
             fraction digits — so a stored 1.2345 would be silently rewritten to
             1.235 by nothing more than tabbing through the field. 20 digits and
             no grouping make that round-trip lossless, which is what the plain
             <input type="number"> did for free. */
          formatOptions={{ maximumFractionDigits: 20, useGrouping: false }}
        >
          {label}
          {description}
          <NumberField.Group>
            <NumberField.DecrementButton />
            <NumberField.Input />
            <NumberField.IncrementButton />
          </NumberField.Group>
        </NumberField>
      );
    }

    case 'boolean':
      // A Checkbox rather than a Switch: a Switch reads as "this takes effect
      // now", and nothing here is applied until the page's Save. It also keeps
      // one tick-box vocabulary with multi_select below.
      return (
        <Checkbox isSelected={value === true} onChange={onChange} isDisabled={disabled}>
          <Checkbox.Content>
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            {label}
          </Checkbox.Content>
          {/* The only branch whose hint sits BELOW the control: the label is
              inline with the box, so there is no room above it. HeroUI indents
              this slot to line up under the label text. */}
          {description}
        </Checkbox>
      );

    case 'date':
      return (
        // Deliberately NOT a HeroUI DateField. RAC's date components speak
        // @internationalized/date objects, so every value would need parsing in
        // and serialising out — and `parseDate` THROWS on a string it dislikes,
        // which takes the whole page down for one malformed row. The native
        // picker speaks YYYY-MM-DD, the exact string this field has always sent,
        // and renders blank for anything it cannot read. TextField here supplies
        // only the label wiring and the field styling; the control underneath is
        // still <input type="date">.
        <TextField
          type="date"
          // <input type="date"> speaks YYYY-MM-DD; the API stores full ISO.
          value={typeof value === 'string' ? value.slice(0, 10) : ''}
          onChange={(next) => onChange(next === '' ? null : next)}
          isDisabled={disabled}
          fullWidth
        >
          {label}
          {description}
          <Input />
        </TextField>
      );

    case 'single_select':
      return (
        <Select
          // Empty is `null`, not `''` — RAC shows the placeholder for it, which
          // is the em dash the blank <option> used to show.
          value={typeof value === 'string' && value !== '' ? value : null}
          onChange={(key) => onChange(key === null || key === CLEAR_KEY ? null : String(key))}
          isDisabled={disabled}
          fullWidth
          placeholder="—"
        >
          {label}
          {description}
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            {/* `textValue` is not redundant with the children: adding the
                selected-row tick makes the children non-plain-text, and RAC
                then has nothing to drive type-to-select or the accessible name
                from. It warns about exactly this in development. */}
            <ListBox>
              <ListBoxItem id={CLEAR_KEY} textValue="—">
                —
                <ListBoxItem.Indicator />
              </ListBoxItem>
              {def.options?.map((option) => (
                <ListBoxItem key={option} id={option} textValue={option}>
                  {option}
                  <ListBoxItem.Indicator />
                </ListBoxItem>
              ))}
            </ListBox>
          </Select.Popover>
        </Select>
      );

    case 'multi_select': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        // CheckboxGroup's value IS a string array, so the wire format survives
        // untouched — including the two properties the hand-rolled version had:
        // a newly ticked option is appended at the end, and a stored value that
        // is no longer in `def.options` is left alone rather than dropped on the
        // next tick.
        <CheckboxGroup value={selected} onChange={onChange} isDisabled={disabled}>
          {label}
          {description}
          {def.options?.map((option) => (
            <Checkbox key={option} value={option}>
              <Checkbox.Content>
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Label>{option}</Label>
              </Checkbox.Content>
            </Checkbox>
          ))}
        </CheckboxGroup>
      );
    }

    case 'text':
    default:
      // Also the fallback for a `valueType` this build has never heard of — an
      // admin-seeded catalogue can outlive the bundle that renders it, and a
      // text box is the one control that can hold anything.
      return (
        <TextField
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          isDisabled={disabled}
          fullWidth
        >
          {label}
          {description}
          <Input />
        </TextField>
      );
  }
}
